import { Stack } from "@mantine/core";
import { useEffect } from "react";
import type { FieldPath } from "react-hook-form";
import { Controller } from "react-hook-form";
import {
  FoerderRegime,
  MieterstromzuschlagApplicationIntent
} from "../../../../types/api.types";
import { getNeedsJahrDesGebotstermins } from "../../../../utils/getNeedsJahrDesGebotstermins";
import { validateEegAnlagenschluessel } from "../../../../utils/validateEegAnlagenschluessel";
import { FormFieldController } from "../../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { FormSection } from "../../../BuildingBlocks/Forms/FormSection/FormSection";
import Paragraph24Zusammenfassung from "../../../EEGAnlage/Paragraph24Zusammenfassung/Paragraph24Zusammenfassung";
import { GENERATOR_FORM_DATA } from "../GeneratorModal.constants";
import type {
  GeneratorFormSectionProps,
  GeneratorSingleEndpointResponse
} from "../GeneratorModal.types";
import { CompensationAndMarketingSection } from "./sections/CompensationAndMarketingSection";
import { EegStundenregelField } from "./sections/EegStundenregelField";

interface EEGAnlageSectionProps extends GeneratorFormSectionProps {
  variantId?: number;
}

export const COMPENSATION_AND_MARKETING_FIELD_NAMES = [
  GENERATOR_FORM_DATA["einheit.eeg_anlage.anzulegender_wert"].name,
  GENERATOR_FORM_DATA["einheit.eeg_anlage.veraeusserungsformen"].name
];

export const getEegTabIgnoredMissingFields = (
  foerderregime?: FoerderRegime | null
) =>
  foerderregime === FoerderRegime.foerderung_ausgelaufen
    ? COMPENSATION_AND_MARKETING_FIELD_NAMES
    : [];

function EEGAnlageTab({ generatorForm, variantId }: EEGAnlageSectionProps) {
  const { control, watch, setValue } = generatorForm;
  const generatorId = watch("id");
  const p24defaultGenerators = watch(
    "einheit.eeg_anlage.paragraph_24_zusammenfassung.default_generators"
  );
  const p24Warning = watch(
    "einheit.eeg_anlage.paragraph_24_zusammenfassung.warning"
  );
  const foerderregime = watch("einheit.eeg_anlage.foerderregime");
  const eegCommissioningDate = watch(
    "einheit.eeg_anlage.eeg_inbetriebnahmedatum"
  );
  const anzulegender_wert = watch("einheit.eeg_anlage.anzulegender_wert");
  const isAnyRegulatoryDataFieldFilled = watch([
    "initial_installed_capacity",
    "building_installed",
    "residential_building_installed",
    "eligible_for_non_building_installed_feedin_tariff",
    "registered_for_mieterstrom"
  ]).some(Boolean);

  const shouldShowJahrDesGebotstermins =
    !!foerderregime && getNeedsJahrDesGebotstermins(foerderregime);

  const jahrDesGebotstermins = watch(
    "einheit.eeg_anlage.jahr_des_gebotstermins"
  );

  const appliedP51Rule = watch("einheit.eeg_anlage.applied_p51_rule");
  const eegVersion = watch("einheit.eeg_anlage.eeg_version");
  const p51RuleIsRelevant = watch("einheit.eeg_anlage.p51_rule_is_relevant");

  const eligibleForMieterstromzuschlag = watch(
    "eligible_for_mieterstromzuschlag"
  );
  const isMieterstromzuschlagEligible = eligibleForMieterstromzuschlag === true;
  const mieterstromzuschlagIntent = watch(
    "mieterstromzuschlag.application_intent"
  );
  const mieterstromzuschlagApplicationDate = watch(
    "mieterstromzuschlag.application_date"
  );
  const showMieterstromzuschlagDate =
    mieterstromzuschlagIntent ===
    MieterstromzuschlagApplicationIntent.ApplyOrAlreadyApplied;

  const shouldShowGebotstermin =
    shouldShowJahrDesGebotstermins && jahrDesGebotstermins === 2025;

  const isKWKFoerderregime =
    foerderregime === FoerderRegime.kwkg_zuschlag_2016_2020 ||
    foerderregime === FoerderRegime.kwkg_zuschlag_2012 ||
    foerderregime === FoerderRegime.kwkg_ausschreibung ||
    foerderregime === FoerderRegime.kwkg_zuschlag_2000_2002_2009;

  const showCompensationAndMarketingSection =
    foerderregime &&
    foerderregime !== FoerderRegime.keine_foerderung_in_anspruch_genommen &&
    !isKWKFoerderregime;

  const isFoerderungAusgelaufen =
    foerderregime === FoerderRegime.foerderung_ausgelaufen;

  useEffect(() => {
    if (
      anzulegender_wert?.[0]?.first_date === null &&
      showCompensationAndMarketingSection
    ) {
      const updatedRows = anzulegender_wert.map((row, index) => {
        if (row === null) return null;
        return {
          ...row,
          first_date: index === 0 ? eegCommissioningDate || "" : row.first_date,
          price_ct_per_kwh:
            row.price_ct_per_kwh !== undefined ? row.price_ct_per_kwh : null
        };
      });
      setValue("einheit.eeg_anlage.anzulegender_wert", updatedRows, {
        shouldValidate: true
      });
    }
  }, [
    anzulegender_wert,
    setValue,
    eegCommissioningDate,
    showCompensationAndMarketingSection
  ]);

  const veraeusserungsformen = watch(
    "veraeusserungsformen" as FieldPath<GeneratorSingleEndpointResponse>
  ) as unknown[] | undefined;

  useEffect(() => {
    if (
      !showCompensationAndMarketingSection &&
      (veraeusserungsformen?.length ?? 0) > 0
    ) {
      setValue(
        "veraeusserungsformen" as FieldPath<GeneratorSingleEndpointResponse>,
        []
      );
    }
  }, [showCompensationAndMarketingSection, veraeusserungsformen, setValue]);

  useEffect(() => {
    if (!showMieterstromzuschlagDate && !!mieterstromzuschlagApplicationDate) {
      setValue("mieterstromzuschlag.application_date", null, {
        shouldValidate: true
      });
    }
  }, [
    mieterstromzuschlagApplicationDate,
    setValue,
    showMieterstromzuschlagDate
  ]);

  return (
    <Stack gap={0}>
      <FormFieldController
        control={control}
        data={GENERATOR_FORM_DATA["einheit.eeg_anlage.im_mastr_registriert"]}
      />
      <FormFieldController
        control={control}
        data={GENERATOR_FORM_DATA["einheit.eeg_anlage.mastr_nummer"]}
      />
      <FormFieldController
        control={control}
        data={GENERATOR_FORM_DATA["einheit.eeg_anlage.anlagenschluessel_eeg"]}
        rules={{
          validate: (value) => {
            if (!value) {
              return true;
            } else {
              return validateEegAnlagenschluessel(value);
            }
          }
        }}
      />
      <FormFieldController
        control={control}
        data={GENERATOR_FORM_DATA["einheit.eeg_anlage.eeg_inbetriebnahmedatum"]}
      />
      <FormFieldController
        control={control}
        data={GENERATOR_FORM_DATA["einheit.eeg_anlage.installierte_leistung"]}
      />
      <Controller
        control={control}
        name={
          GENERATOR_FORM_DATA[
            "einheit.eeg_anlage.paragraph_24_zusammenfassung.custom_generator_ids"
          ].name
        }
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <Paragraph24Zusammenfassung
            defaultGenerators={p24defaultGenerators}
            error={error}
            lockedValues={generatorId ? [generatorId] : []}
            value={value}
            warning={p24Warning}
            onChange={(value) => {
              onChange(value);
            }}
          />
        )}
      />

      <FormSection label="Gesetzliche Förderung der Anlage">
        <FormFieldController
          control={control}
          data={{
            ...GENERATOR_FORM_DATA["einheit.eeg_anlage.foerderregime"],
            warning:
              !shouldShowJahrDesGebotstermins && jahrDesGebotstermins
                ? "Für diese Anlage ist im Marktstammdatenregister eine Zuschlagsnummer hinterlegt, was auf eine Ausschreibung hindeutet. Das gewählte Förderregime passt nicht dazu. Bitte fahren Sie nur fort, wenn Sie sicher sind."
                : undefined
          }}
        />

        {shouldShowJahrDesGebotstermins && (
          <FormFieldController
            control={control}
            data={
              GENERATOR_FORM_DATA["einheit.eeg_anlage.jahr_des_gebotstermins"]
            }
          />
        )}
        {shouldShowGebotstermin && (
          <FormFieldController
            control={control}
            data={GENERATOR_FORM_DATA["einheit.eeg_anlage.gebotstermin"]}
          />
        )}

        {p51RuleIsRelevant && (
          <EegStundenregelField
            appliedP51Rule={appliedP51Rule}
            eegVersion={eegVersion}
          />
        )}

        {showCompensationAndMarketingSection && (
          <CompensationAndMarketingSection
            eegCommissioningDate={eegCommissioningDate}
            foerderregime={foerderregime}
            generatorForm={generatorForm}
            generatorId={generatorId}
            isFoerderungAusgelaufen={isFoerderungAusgelaufen}
            variantId={variantId}
          />
        )}
      </FormSection>
      {isAnyRegulatoryDataFieldFilled && (
        <FormSection label="Regulatorische Daten">
          <FormFieldController
            control={control}
            data={GENERATOR_FORM_DATA.initial_installed_capacity}
          />
          <FormFieldController
            control={control}
            data={GENERATOR_FORM_DATA.building_installed}
          />
          <FormFieldController
            control={control}
            data={GENERATOR_FORM_DATA.residential_building_installed}
          />
          <FormFieldController
            control={control}
            data={
              GENERATOR_FORM_DATA.eligible_for_non_building_installed_feedin_tariff
            }
          />
          <FormFieldController
            control={control}
            data={GENERATOR_FORM_DATA.registered_for_mieterstrom}
          />
        </FormSection>
      )}

      {isMieterstromzuschlagEligible && (
        <FormSection label="Mieterstromzuschlag">
          <FormFieldController
            control={control}
            data={GENERATOR_FORM_DATA["mieterstromzuschlag.application_intent"]}
          />
          {showMieterstromzuschlagDate && (
            <FormFieldController
              control={control}
              data={GENERATOR_FORM_DATA["mieterstromzuschlag.application_date"]}
            />
          )}
        </FormSection>
      )}
    </Stack>
  );
}

export { EEGAnlageTab };
